import { Button } from '@cocoonspace/ui/components/button'
import { useLingui } from '@lingui/react/macro'
import { authEmitter } from '@cocoonspace/shared/domains/auth/emitters/auth.emitter'
import {
	getAdditionalUserInfo,
	GoogleAuthProvider,
	OAuthProvider,
	signInWithPopup,
} from 'firebase/auth'
import { toast } from 'sonner'

import { SvgApple } from '~/components/icons/apple'
import { GoogleIcon } from '~/components/icons/google'
import { MicrosoftIcon } from '~/components/icons/microsoft'
import { useHttpErrorMsg } from '~/lib/get-http-error-msg'
import { getAuthInstance } from '~/providers/auth-provider'
import { LastUsedBadge, useLastUsed } from './last-use'

export const GoogleConnectButton = ({
	action,
	onSuccess,
}: {
	action: 'login' | 'register'
	onSuccess?: () => void
}) => {
	const { t } = useLingui()
	const httpErrorMsg = useHttpErrorMsg()
	const { lastUsed, markUsed } = useLastUsed()

	const label =
		action === 'register'
			? t`S'inscrire avec Google`
			: t`Se connecter avec Google`

	const handleSignInWithGoogle = () => {
		const auth = getAuthInstance()
		const provider = new GoogleAuthProvider()

		return signInWithPopup(auth, provider)
			.then((res) => {
				markUsed('google')

				if (action === 'login') return

				if (getAdditionalUserInfo(res)?.isNewUser) {
					authEmitter.emit('register.success')
				}

				onSuccess?.()
			})
			.catch((error) => {
				const errorCode = error.code as string
				const errorMessage =
					httpErrorMsg[errorCode]?.() || t`Une erreur est survenue`

				toast.error(errorMessage)
			})
	}

	return (
		<Button
			type='button'
			className='hover:border-foreground hover:text-foreground relative w-full gap-x-2'
			onClick={handleSignInWithGoogle}
			variant='outline'
		>
			<GoogleIcon />
			<span className='font-bold'>{label}</span>
			{lastUsed === 'google' && <LastUsedBadge />}
		</Button>
	)
}

export const AppleConnectButton = ({
	action,
	onSuccess,
}: {
	action: 'login' | 'register'
	onSuccess?: () => void
}) => {
	const { t } = useLingui()
	const httpErrorMsg = useHttpErrorMsg()
	const { lastUsed, markUsed } = useLastUsed()

	const label =
		action === 'register'
			? t`S'inscrire avec Apple`
			: t`Se connecter avec Apple`

	const handleSignInWithApple = () => {
		const auth = getAuthInstance()
		const provider = new OAuthProvider('apple.com')

		return signInWithPopup(auth, provider)
			.then((res) => {
				markUsed('apple')

				if (action === 'login') return

				if (getAdditionalUserInfo(res)?.isNewUser) {
					authEmitter.emit('register.success')
				}

				onSuccess?.()
			})
			.catch((error) => {
				const errorCode = error.code as string
				const errorMessage =
					httpErrorMsg[errorCode]?.() || t`Une erreur est survenue`

				toast.error(errorMessage)
			})
	}
	return (
		<Button
			type='button'
			className='relative w-full gap-x-2 bg-black hover:bg-black'
			onClick={handleSignInWithApple}
		>
			<SvgApple className='mb-[3px] text-lg' />
			<span className='font-bold'>{label}</span>
			{lastUsed === 'apple' && <LastUsedBadge />}
		</Button>
	)
}

export const MicrosoftConnectButton = ({
	action,
	onSuccess,
}: {
	action: 'login' | 'register'
	onSuccess?: () => void
}) => {
	const { t } = useLingui()
	const httpErrorMsg = useHttpErrorMsg()
	const { lastUsed, markUsed } = useLastUsed()

	const label =
		action === 'register'
			? t`S'inscrire avec Microsoft`
			: t`Se connecter avec Microsoft`

	const handleSignInWithMicrosoft = () => {
		const auth = getAuthInstance()
		const provider = new OAuthProvider('microsoft.com')

		return signInWithPopup(auth, provider)
			.then((res) => {
				markUsed('microsoft')

				if (action === 'login') return

				if (getAdditionalUserInfo(res)?.isNewUser) {
					authEmitter.emit('register.success')
				}

				onSuccess?.()
			})
			.catch((error) => {
				const errorCode = error.code as string
				const errorMessage =
					httpErrorMsg[errorCode]?.() || t`Une erreur est survenue`

				toast.error(errorMessage)
			})
	}

	return (
		<Button
			type='button'
			className='hover:border-foreground hover:text-foreground relative w-full gap-x-2'
			onClick={handleSignInWithMicrosoft}
			variant='outline'
		>
			<MicrosoftIcon />
			<span className='font-bold'>{label}</span>
			{lastUsed === 'microsoft' && <LastUsedBadge />}
		</Button>
	)
}
